/**
 * Shared constants and utilities for the API client modules.
 */

import { API_BASE as _API_BASE } from '../config.js';
export const API_BASE = _API_BASE;

/** Client version sent on every API request for backend version negotiation */
const CLIENT_VERSION = `reader/${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'}`;

/**
 * Build standard headers for API requests.
 * Includes version header; adds Authorization if token provided.
 */
export function apiHeaders(token, contentType = 'application/json') {
    const h = { 'X-Client-Version': CLIENT_VERSION };
    if (contentType) h['Content-Type'] = contentType;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
}

/**
 * HTTP status codes worth retrying automatically.
 *
 * 408 Request Timeout, 425 Too Early, 429 Too Many Requests are retryable
 * client-side signals. 500/502/503/504 are transient backend failures —
 * in our stack `D1 DB storage operation exceeded timeout` surfaces as 500,
 * so retries here meaningfully absorb Cloudflare D1 replica hiccups.
 *
 * Deliberately NOT retried: 400, 401, 403, 404, 409, 422 — these indicate
 * either a client bug or a real semantic failure (token expired, resource
 * missing, validation error). Retrying them would mask the problem and waste
 * battery / quota.
 */
const TRANSIENT_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

/**
 * fetch() with exponential-backoff retry for transient failures.
 *
 * Typical use: GET requests to read-only endpoints. For POST/PUT/DELETE,
 * retry is safe only when the endpoint is idempotent (UPSERT-style), not
 * when it produces additive side effects (e.g. incrementing a counter).
 * Caller is responsible for judging idempotency.
 *
 * Retry policy:
 *   - Up to `maxRetries` additional attempts (default 2, total 3 requests).
 *   - Triggered by: network errors (TypeError — DNS fail / CORS / offline),
 *     or HTTP status in `TRANSIENT_STATUSES`.
 *   - Exponential backoff with ±25% jitter: 300ms, 900ms, 2700ms, …
 *   - Fast-abort: if the caller passed a signal that was already aborted,
 *     we propagate immediately without retry.
 *
 * On final failure, returns the last `Response` (for HTTP errors) or rethrows
 * the last network error — the caller's existing error-handling path is
 * preserved. We never fabricate a fake Response.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {{ maxRetries?: number, baseDelayMs?: number, retryOn?: (status:number)=>boolean }} [retryOpts]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, options = {}, retryOpts = {}) {
    const maxRetries = retryOpts.maxRetries ?? 2;
    const baseDelayMs = retryOpts.baseDelayMs ?? 300;
    const retryOn = retryOpts.retryOn ?? ((status) => TRANSIENT_STATUSES.has(status));

    let lastError = null;
    let lastResponse = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        // Respect caller-side abort: don't start a fresh attempt if already cancelled.
        if (options.signal?.aborted) {
            throw options.signal.reason || new DOMException('aborted', 'AbortError');
        }

        try {
            const resp = await fetch(url, options);
            if (resp.ok || !retryOn(resp.status)) {
                return resp;
            }
            lastResponse = resp;
            lastError = null;
        } catch (err) {
            // Don't retry explicit aborts from the caller — it means the
            // caller (e.g. component unmount) no longer wants the result.
            if (err?.name === 'AbortError') throw err;
            lastError = err;
            lastResponse = null;
        }

        if (attempt === maxRetries) break;

        // Exponential backoff with jitter: baseDelay * 3^attempt ± 25%.
        // The long tail (300 → 900 → 2700ms) gives D1 colo enough time to
        // recover without making user-visible latency horrendous.
        const delay = baseDelayMs * Math.pow(3, attempt);
        const jittered = delay * (0.75 + Math.random() * 0.5);
        await new Promise((r) => setTimeout(r, jittered));
    }

    if (lastResponse) return lastResponse;
    throw lastError ?? new Error('fetchWithRetry: unknown failure');
}

// Cache for blob URLs to avoid recreating them
export const blobUrlCache = new Map();

export function dataUrlToBlob(dataUrl) {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}
